import { i18nService } from "cruzo";

export const SUPPORTED_UI_LANGS = ["ru", "en", "fr"] as const;
export type UiLang = (typeof SUPPORTED_UI_LANGS)[number];

const LEGACY_STORAGE_KEY = "ui_lang";
const STORAGE_KEY = "cruzo.i18n.lang";

export function isUiLang(value: string): value is UiLang {
  return value === "ru" || value === "en" || value === "fr";
}

function migrateLegacyStorage(): void {
  try {
    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!legacy || !isUiLang(legacy) || localStorage.getItem(STORAGE_KEY)) {
      return;
    }
    localStorage.setItem(STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
  }
}

function readStoredUiLang(): UiLang | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && isUiLang(stored) ? stored : null;
  } catch {
    return null;
  }
}

function firstSupportedBrowserLang(): UiLang | null {
  if (typeof navigator === "undefined") {
    return null;
  }

  const candidates = navigator.languages?.length
    ? navigator.languages
    : navigator.language
      ? [navigator.language]
      : [];

  for (const raw of candidates) {
    const short = raw?.toLowerCase().split("-")[0];
    if (short && isUiLang(short)) {
      return short;
    }
  }

  return null;
}

export function normalizeUiLang(raw: string): UiLang {
  if (isUiLang(raw)) {
    return raw;
  }
  return firstSupportedBrowserLang() ?? "ru";
}

function syncDocumentLang(): void {
  document.documentElement.lang = normalizeUiLang(i18nService.lang$.actual);
}

let documentLangBound = false;

function bindDocumentLangSync(): void {
  if (documentLangBound) {
    return;
  }
  documentLangBound = true;
  i18nService.lang$.setPostUpdate(() => syncDocumentLang());
}

export function initI18n(): void {
  migrateLegacyStorage();
  i18nService.setDefaultLang("ru");

  const stored = readStoredUiLang();
  const lang = stored ?? normalizeUiLang(i18nService.detectLang());

  if (stored) {
    i18nService.setLang(stored);
  } else if (lang !== i18nService.lang$.actual) {
    i18nService.lang$.update(lang);
  }

  syncDocumentLang();
  bindDocumentLangSync();
}

export function setUiLang(lang: UiLang): void {
  i18nService.setLang(lang);
}

export function uiLang(): UiLang {
  return normalizeUiLang(i18nService.lang$.actual);
}

export { i18nService };
